import React, { FC, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Tab, Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"

import { BalanceProcessorTable } from "features/balance/BalanceProcessorTable"
import { BalanceToolbar } from "features/balance/BalanceToolbar"
import { PaymentRoutesPanel } from "features/balance/PaymentRoutesPanel"
import { SettlementReportModal } from "features/balance/SettlementReportModal"
import { TopUpModal } from "features/balance/TopUpModal"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { tableBalanceMethods, tableBalanceMethodsWithdrawal, tableBalanceProcessors } from "constants/data"
import { TableDataType } from "constants/eums"
import { TableColumnsInterface } from "constants/interfaces"
import { PERMISSIONS } from "constants/permissions"

import { balanceService } from "services/balance/balance.service"
import { BalanceTxType } from "services/balance/type"

enum BalanceTab {
  DEPOSIT = "DEPOSIT",
  WITHDRAWAL = "WITHDRAWAL",
  ROUTES = "ROUTES"
}

export const BalancePage: FC = () => {
  const { darkMode } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })
  const isAdmin = has(PERMISSIONS.PAGE_BALANCE)

  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<string>(BalanceTab.DEPOSIT)
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [reloadToken, setReloadToken] = useState(0)
  const [resetConfirm, setResetConfirm] = useState(false)
  const [resetTarget, setResetTarget] = useState<{ processor?: string; payment_method?: string } | null>(null)
  const [reportModal, setReportModal] = useState(false)
  const [topUpTarget, setTopUpTarget] = useState<{ processor: string; payment_method: string } | null>(null)

  const reload = () => setReloadToken((v) => v + 1)

  const txType: BalanceTxType = activeTab === BalanceTab.WITHDRAWAL ? "WITHDRAWAL" : "DEPOSIT"

  const handleResetAvailable = async () => {
    const success = await balanceService.resetAvailable({
      tx_type: txType,
      processor: resetTarget?.processor,
      payment_method: resetTarget?.payment_method
    })
    if (success) reload()
    setResetTarget(null)
  }

  const handleRowReset = (e: React.MouseEvent, row: any) => {
    e.stopPropagation()
    setResetTarget({ processor: row.processor, payment_method: row.payment_method })
    setResetConfirm(true)
  }

  const handleRowTopUp = (e: React.MouseEvent, row: any) => {
    e.stopPropagation()
    setTopUpTarget({ processor: row.processor, payment_method: row.payment_method })
  }

  const handleResetAll = () => {
    setResetTarget(null)
    setResetConfirm(true)
  }

  const handleDismissReset = () => {
    setResetConfirm(false)
    setResetTarget(null)
  }

  const getResetConfirmText = () => {
    if (resetTarget?.payment_method) {
      return t("balance.resetAvailableConfirmMethod", { processor: resetTarget.processor, method: resetTarget.payment_method })
    }
    if (resetTarget?.processor) {
      return t("balance.resetAvailableConfirmProcessor", { processor: resetTarget.processor })
    }
    return t("balance.resetAvailableConfirm")
  }

  const tabs: Tab[] = useMemo(
    () => [
      { key: BalanceTab.DEPOSIT, label: t("balance.deposits") },
      { key: BalanceTab.WITHDRAWAL, label: t("balance.withdrawals") },
      { key: BalanceTab.ROUTES, label: t("balance.paymentRoutes") }
    ],
    [t]
  )

  const rangeParams = useMemo(() => {
    const start = timeframe?.[0] ?? null
    const end = timeframe?.[1] ?? null

    return {
      tx_type: txType,
      start: start ? start.toISOString() : undefined,
      end: end ? end.toISOString() : undefined,
      _reload: reloadToken
    }
  }, [timeframe, txType, reloadToken])

  const resetActionCol: TableColumnsInterface = useMemo(
    () => ({
      value: "_reset",
      title: "",
      format: TableDataType.STRING,
      sorting: false,
      customRender: (_v: any, row: any) =>
        isAdmin ? (
          <button
            onClick={(e) => handleRowReset(e, row)}
            className="px-3 py-1 rounded text-xs font-medium border border-rose-500 text-rose-500 hover:bg-rose-50 transition-all dark:text-rose-400 dark:border-rose-400 dark:hover:bg-rose-900/20 whitespace-nowrap"
          >
            {t("balance.resetAvailable")}
          </button>
        ) : null
    }),
    [t, isAdmin]
  )

  const topUpActionCol: TableColumnsInterface = useMemo(
    () => ({
      value: "_topup",
      title: "",
      format: TableDataType.STRING,
      sorting: false,
      customRender: (_v: any, row: any) =>
        isAdmin ? (
          <button
            onClick={(e) => handleRowTopUp(e, row)}
            className="px-3 py-1 rounded text-xs font-medium border border-emerald-600 text-emerald-600 hover:bg-emerald-50 transition-all dark:text-emerald-400 dark:border-emerald-400 dark:hover:bg-emerald-900/20 whitespace-nowrap"
          >
            {t("balance.topUp")}
          </button>
        ) : null
    }),
    [t, isAdmin]
  )

  const processorColumns = useMemo<TableColumnsInterface[]>(() => [...tableBalanceProcessors, resetActionCol], [resetActionCol])
  const depositMethodColumns = useMemo<TableColumnsInterface[]>(() => [...tableBalanceMethods, resetActionCol], [resetActionCol])
  const withdrawalMethodColumns = useMemo<TableColumnsInterface[]>(
    () => [...tableBalanceMethodsWithdrawal, topUpActionCol, resetActionCol],
    [topUpActionCol, resetActionCol]
  )

  return (
    <div className={clsx(darkMode && "dark")}>
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("balance.title")} />

        <div className="mt-4 rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-4">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-start">
              <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} variant="pills" />
            </div>

            {activeTab !== BalanceTab.ROUTES && (
              <BalanceToolbar
                txType={txType}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
                onReloadNeeded={reload}
                onResetAll={handleResetAll}
                onOpenReport={() => setReportModal(true)}
                isAdmin={isAdmin}
              />
            )}

            {activeTab === BalanceTab.ROUTES ? (
              <PaymentRoutesPanel loading={loading} setLoading={setLoading} />
            ) : (
              <BalanceProcessorTable
                txType={txType}
                processorColumns={processorColumns}
                methodColumns={activeTab === BalanceTab.WITHDRAWAL ? withdrawalMethodColumns : depositMethodColumns}
                rangeParams={rangeParams}
              />
            )}
          </div>
        </div>

        <ModalConfirmAction
          data={resetConfirm}
          handler={handleDismissReset}
          text={getResetConfirmText()}
          actionText={t("balance.resetAvailable")}
          actionHandler={handleResetAvailable}
        />

        <SettlementReportModal open={reportModal} onClose={() => setReportModal(false)} />

        <TopUpModal
          open={!!topUpTarget}
          processor={topUpTarget?.processor}
          payment_method={topUpTarget?.payment_method}
          onClose={() => setTopUpTarget(null)}
          onSuccess={reload}
        />
      </div>
    </div>
  )
}
